import { ROUTE } from "./ROUTE";

export interface PageMeta {
  title: string;
  description?: string;
  keywords?: string;
  noIndex?: boolean;
}

type RouteValue = (typeof ROUTE)[keyof typeof ROUTE];

export const PAGE_META: Partial<Record<RouteValue, PageMeta>> = {
  [ROUTE.HOME]: {
    title: "MyDecisive | The Autonomous Telemetry Hub for Decisive Operations",
    description:
      "Close the observability control gap with MyDecisive SmartHub. Automate OTel runbooks on the wire to redact PII, optimize costs, and drive MTTR toward zero",
  },
  [ROUTE.PLATFORM]: {
    title: "MyDecisive | SmartHub: Autonomous Telemetry & OTel Automation",
    description:
      "Stop observing and start acting. Use MyDecisive SmartHub's IFTTT engine to automate complex runbooks in-stream, saving millions in vendor ingestion costs",
  },
  [ROUTE.ALL_SOLUTIONS]: {
    title:
      "MyDecisive | Observability Solutions: Autonomous Telemetry & OTel Control",
    description:
      "Explore MyDecisive's autonomous observability solutions. Leverage the SmartHub to redact PII, slash ingest costs, and drive MTTR to zero. Learn more.",
  },
  [ROUTE.OCTANT_PARENT]: {
    title: "Octant Applets | Modular Telemetry Control - MyDecisive.ai",
    description:
      "Octant is your core interface to MyDecisive, giving you absolute control over telemetry with a modular, snap-in Applet ecosystem. Optimize telemetry spend and streamline operational automation.",
  },
  [ROUTE.OCTANT_HOW_IT_WORKS]: {
    title: "Octant's Architecture | Autonomous Telemetry Hub - MyDecisive.ai",
    description:
      "Octant is the autonomous telemetry hub for decisive operations — open source, GitOps-ready, built on OpenTelemetry and Kubernetes. Intercept, route, and act on telemetry at the wire layer.",
  },
  [ROUTE.OCTANT_CONNECT]: {
    title: "Octant Connect | Automated Pipeline Generation - MyDecisive.ai",
    description:
      "Octant Connect instantly generates production-grade, GitOps-ready data pipelines. Automatically wire MyDecisive into your telemetry flow with unified multi-cloud visibility and automated governance.",
  },
  [ROUTE.OCTANT_CLARITY]: {
    title: "Octant Clarity | Telemetry Cost Control, No Vendor Tax - MyDecisive.ai",
    description:
      "Octant Clarity stops you overpaying for telemetry data. A fully OTel-native, BYOC architecture gives you total control over what data you send, where you route it, and why.",
  },
  [ROUTE.OCTANT_PULSE]: {
    title: "Octant Pulse | Real-Time Anomaly Detection - MyDecisive.ai",
    description:
      "Octant Pulse delivers real-time anomaly detection on the wire, 24x7x365. A self-managed, zero-setup seasonality model continuously learns your baseline and monitors every layer of your architecture.",
  },
  [ROUTE.OCTANT_PRICING]: {
    title: "Pricing | Simple Plans for Every Stage - MyDecisive.ai",
    description:
      "SmartHub is always open source and free. Paid Octant applets start at ~$2,000/mo, unlocking advanced wire-layer telemetry intelligence, anomaly detection, and error correlation as your team scales.",
  },
  [ROUTE.OCTANT_OPEN_SOURCE]: {
    title: "MyDecisive Open Source | Octant & SmartHub - Free Observability Platform",
    description:
      "Octant and SmartHub are open source and free to use, contribute to, and run anywhere. Open source core with paid intelligence add-ons like Pulse and Clarity Plus for anomaly detection and error correlation.",
    keywords:
      "MyDecisive, Octant, SmartHub, open source observability, telemetry, anomaly detection, MTTR, SecOps, data sovereignty, wire-level signal",
  },
  [ROUTE.INTELLIGENT_LOGSTREAM]: {
    title: "MyDecisive | Control Log Noise and Costs On the Wire",
    description:
      "Stop paying for legacy log noise. The MyDecisive SmartHub provides secure observability on the wire to redact PII and route logs in real-time. Save millions",
  },
  [ROUTE.INTELLIGENT_ALERTING]: {
    title: "MyDecisive | End Alert Fatigue with Real-Time Stateful Alerting",
    description:
      "Stop alert fatigue. MyDecisive SmartHub uses stateful logic and braided telemetry to eliminate false positives. Get 100% coverage with zero-work OTel alerting",
  },
  [ROUTE.INTELLIGENT_TRACE_SAMPLING]: {
    title:
      "MyDecisive | SmartHub: Perfected Trace Sampling & Stateful Alerting",
    description:
      "Experience Non-Sampling Sampling. Capture 100% of critical traces with late-decision logic. Get perfect visibility without the vendor ingestion tax. See how",
  },
  [ROUTE.PII_REDACTION]: {
    title: "MyDecisive | SmartHub: In-Flight PII Redaction & Data Control",
    description:
      "Reclaim your data sovereignty. Stop paying vendors to delete your data. MyDecisive SmartHub provides in-flight PII redaction on the wire. Secure your OTel stream",
  },
  [ROUTE.PARTNERS]: {
    title:
      "MyDecisive | Partner with us and drive revenue with autonomous enterprise AI",
    description:
      "Join the MyDecisive Partner Program. Deliver autonomous observability that slashes client costs by 80% and drives MTTR to zero. Scale your services today",
  },
  [ROUTE.ABOUT]: {
    title:
      "MyDecisive | Meet the Team Behind Defining Autonomous Observability",
    description:
      "Meet the team moving IT from reactive to autonomous. Led by Ari Zilka and veterans of Salesforce, Walmart, Hortonworks and New Relic, MyDecisive slashes costs and drives MTTR to zero.",
  },
  [ROUTE.BLOG]: {
    title:
      "MyDecisive | The Autonomous IT Blog: Insights on OTel & Observability",
    description:
      "The definitive source for autonomous observability. Learn how to transform passive telemetry into active control with MyDecisive’s expert guides and OTel news.",
  },
  [ROUTE.EVENTS]: {
    title: "MyDecisive | Observability Events & OTel Meetups",
    description:
      "Connect with MyDecisive at major 2026 observability events. Join Ari Zilka and our team of experts to discuss autonomous OTel and zero-MTTR operations.",
  },
  [ROUTE.CASE_STUDY]: {
    title: "MyDecisive | Case Studies",
    description:
      "Explore MyDecisive case studies and see how autonomous telemetry reduces costs and improves incident response outcomes.",
  },
  [ROUTE.CAREERS]: {
    title: "MyDecisive | Join the Team Defining Autonomous IT",
    description:
      "Build the future of autonomous telemetry. Join Ari Zilka & a remote-first team of experts slashing costs and MTTR. Explore open roles and be decisive today.",
  },
  [ROUTE.NEWS]: {
    title: "MyDecisive | Newsroom: The Latest in Autonomous IT & OTel News",
    description:
      "Stay updated with MyDecisive. Explore our latest press releases, product news, and milestones in autonomous observability, OTel standards, and MTTR control.",
  },
  [ROUTE.TERMS]: {
    title: "MyDecisive | Terms of Use",
    description:
      "Review the Terms of Use for MyDecisive. This agreement outlines the rules, guidelines, and legal terms for using our platform and services.",
  },
  [ROUTE.PRIVACY]: {
    title: "MyDecisive | Privacy Policy: How We Protect Your Data",
    description:
      "Learn how MyDecisive collects, uses, and protects your information. We are committed to data transparency and security.",
  },
  [ROUTE.ADMIN_CONTENT_EDITOR]: {
    title: "Admin | Home Page Editor — MyDecisive",
    description: "",
    noIndex: true,
  },
  [ROUTE.ADMIN]: {
    title: "Admin | MyDecisive",
    description: "",
    noIndex: true,
  },
  [ROUTE.ADMIN_AUTH_ERROR]: {
    title: "Admin Authentication Error | MyDecisive",
    description: "",
    noIndex: true,
  },
};

export const DEFAULT_PAGE_META: PageMeta = {
  title: "MyDecisive",
};
